import dayjs from "dayjs";
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, OnDestroy, OnInit, Renderer2, signal, inject, viewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { combineLatest, Subject, zip } from "rxjs";

import {
  ErrorPageCode,
  PARAM_CONVERSATION_ID,
  PARAM_ROOM_ID,
  smsFlow,
} from "../../app.enums";
import { debug, trapFocus } from "../../app.utils";
import { IGenesysAgent } from "../../models";
import {
  InteractionService,
  InvitationService,
  QRService,
} from "../../services";
import {
  IWidgetParams as IConfigOptions,
  IMessage,
} from "@auvious/integrations";
import { combineLatestWith, filter, first, take, tap } from "rxjs/operators";
import {
  ActivityIndicatorService,
  AgentParam,
  AppConfigService,
  ApplicationService,
  AppointmentParticipantStateEnum,
  AuviousRtcService,
  CallEndedAction,
  centerInOut,
  ConferenceService,
  ConferenceStore,
  ConversationOriginEnum,
  CustomerParam,
  debugError,
  DeviceService,
  GenericErrorHandler,
  IApplication,
  IAppointment,
  IAppointmentRouting,
  IConversationEventHandlers,
  IHook,
  IInteraction,
  IRecorderInfo,
  IRecorderOptions,
  IUser,
  LocalMediaService,
  MediaRulesService,
  PointerService,
  PublicParam,
  RatingService,
  RecorderService,
  RecorderStateEnum,
  slideInOut,
  TerminateReasonEnum,
  TicketTypeEnum,
  UserService,
  WindowEventService,
} from "../../../core-ui";
import {
  ConferenceMetadataKeyEnum,
  ConversationChannelEnum,
  ConversationDestinationEnum,
  ConversationTypeEnum,
  LayoutEnum,
  OriginModeEnum,
  PARAM_TICKET_ID,
  UserCapabilityEnum,
  UserRoleEnum,
} from "../../../core-ui/core-ui.enums";
import {
  CustomerMetricsMetadata,
  IntegrationMetadata,
  TransferMetadata,
} from "../../../core-ui/models/Metadata";
import { ConfirmNotification } from "../../../core-ui/models/notifications/ConfirmNotification";
import { AnalyticsService } from "../../../core-ui/services/analytics.service";
import { AppointmentService } from "../../../core-ui/services/appointment.service";
import { NotificationService } from "../../../core-ui/services/notification.service";
import { SnapshotService } from "../../../core-ui/services/snapshot.service";
import { ThemeService } from "../../../core-ui/services/theme.service";
import {
  ProtectedTicketService,
  PublicTicketService,
} from "../../../core-ui/services/ticket.service";
import {
  ReadyAction,
  TransferAction,
  windowActionType,
} from "../../../core-ui/services/window.event.service";
import { WIDGET_CUSTOMER_CALL_ACCEPTED_ACK } from "../../app.enums";
import { ConversationService } from "../../services/conversation.service";
import { NgZone } from "@angular/core";
import { InteractionMetricEnum } from "../../../core-ui/core-ui.enums";
import { IEndpointMetadata } from "../../../core-ui/models/IEndpointState";
import { CobrowseService } from "../../../core-ui/services/cobrowse.service";
import { ShareLinkComponent } from "../../../app/components";
import { StreamTypes } from "@auvious/rtc";
import { AuthState } from "../../../core-ui/models/AuthState";

@Component({
  selector: "app-page-conference",
  templateUrl: "./conference.component.html",
  styleUrls: ["./conference.component.scss"],
  animations: [slideInOut, centerInOut],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ConferencePageComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private config = inject(AppConfigService);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private errorHandler = inject(GenericErrorHandler);
  private conferenceService = inject(ConferenceService);
  private windowEventService = inject(WindowEventService);
  private recorderService = inject(RecorderService);
  private qrService = inject(QRService);
  private ratingService = inject(RatingService);
  private applicationService = inject(ApplicationService);
  private route = inject(ActivatedRoute);
  private publicTicketService = inject(PublicTicketService);
  private notification = inject(NotificationService);
  private rtcService = inject(AuviousRtcService);
  private analyticsService = inject(AnalyticsService);
  private interactionService = inject(InteractionService);
  private cdr = inject(ChangeDetectorRef);
  private snapshot = inject(SnapshotService);
  private theme = inject(ThemeService);
  private conversation = inject(ConversationService);
  private appointmentService = inject(AppointmentService);
  store = inject(ConferenceStore);
  private zone = inject(NgZone);
  private pointerService = inject(PointerService);
  private local = inject(LocalMediaService);
  private invitationService = inject(InvitationService);
  private ticketService = inject(ProtectedTicketService);
  private cobrowseService = inject(CobrowseService);
  private activity = inject(ActivityIndicatorService);
  private device = inject(DeviceService);
  private mediaRules = inject(MediaRulesService);
  private userService = inject(UserService);
  private host = inject(ElementRef);
  private render = inject(Renderer2);
  private destroyRef = inject(DestroyRef);

  interaction: IInteraction;
  me: IUser;
  recorderOptions: IRecorderOptions;
  recorderInfo: IRecorderInfo;
  recorderTracked = false;
  activeAgents: IGenesysAgent[] = [];
  QRData: string;
  handlers: IConversationEventHandlers;
  waitingForCustomerTimeout;
  confirmNotificationCustomerTimeout: ConfirmNotification;
  customerTicket: string;
  appointment: IAppointment<IAppointmentRouting>;

  // flags
  isDeviceSetup: boolean;
  isRating = false;
  isInteractionEnding = false;
  isInteractionTransferring = false;
  isOriginWidgetWithoutVideo = false;
  isAccepted = false;
  isCustomerEndingConversation = false;
  isAgentEndingConversation = false;
  isReviewingSnapshots = false;
  isAgentConnected = false;
  isCallStarted = false;
  isConversationEndingTracked = false;
  isAgent = false;
  isCustomer = false;
  isGuestAgent = false;
  isMessagingEnabled = false;
  isMonitoringSupervisor = false;
  isFocusedAfterStreamPublished = false;
  isCallEndingHandled = false;

  shareViaSMSFlow = signal<smsFlow>(undefined);

  $callEnding = new Subject();
  $callEnded = new Subject();
  $recorderStarted = new Subject<IRecorderInfo>();

  readonly shareLinkOptionsRef = viewChild<ShareLinkComponent>("shareLinkOptions");

  constructor() {
    const userService = this.userService;

    this.isDeviceSetup = false;

    this.me = userService.getActiveUser();

    this.isAgent = this.userService.isAgent;
    this.isCustomer = userService.isCustomer;
    this.isGuestAgent = userService.isGuestAgent;
    this.isMessagingEnabled = this.me.hasCapability(
      UserCapabilityEnum.messaging
    );

    this.route.data.subscribe((data) => {
      this.interaction = data.interaction;
    });

    this.qrService.QRLinkAvailable$.subscribe((QRData) => {
      this.QRData = QRData;
      this.cdr.detectChanges();
    });

    if (this.isAgent) {
      const chatTranscriptHook: IHook = {
        run: async () => this.saveChatTranscript(),
      };
      // in case we terminate the conference with an active recorder
      this.conferenceService.registerPreTerminateHook(chatTranscriptHook);

      //in case the recorder stops during a call
      this.recorderService.registerPreStopHook(chatTranscriptHook);

      this.isAccepted = true;
    } else if (this.isCustomer) {
      this.isAccepted = this.interactionService.hasAcceptedTerms();
      if (this.interactionService.hasToAcceptTerms() && this.isAccepted) {
        this.trySaveAcceptedTerms();
      }
    }
  }

  @HostListener("click")
  click() {
    if (!this.isCallStarted) {
      return;
    }
    // this.shareOpen = false;
    this.cdr.detectChanges();
  }

  async ngOnInit() {
    this.observeCallEnd();

    this.isMonitoringSupervisor = !!this.interaction.getWhisperMode(); // change if we support more modes

    if (this.isAgent) {
      this.isConversationEndingTracked = false;

      if (
        !this.isMonitoringSupervisor &&
        !this.isGuestAgent &&
        this.interaction.getChannel() == ConversationChannelEnum.genesysCall &&
        this.config.agentParam(AgentParam.AUTO_HOLD_CONVERSATION_ON_CALL_START)
      ) {
        this.conversation.holdConversation(this.interaction);
      }

      this.conferenceService.localStreamPublished$
        .pipe(
          filter(
            (s) =>
              !!s &&
              (this.application.supportsInteractions() ||
                // we need this for genesys premise
                this.applicationService.isStandaloneApp)
          ),
          take(1),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((_) => {
          // set the integration application id so that it can be retrieved by the customer
          this.conferenceService.setConferenceMetadata(
            new IntegrationMetadata(
              this.conferenceService.myself,
              this.interaction.getId()
            )
          );
        });

      // when we are coming from a transfer, the call is on hold. Take it out.
      // Read hold state imperatively from the RTC session rather than waiting on
      // callHoldStateChange$ (a plain Subject with no replay), which races with
      // the holdUpdated RTC event and can be missed by late subscribers.

      this.conferenceService.localStreamPublished$
        .pipe(
          filter(
            (stream) =>
              !!stream &&
              this.interaction.isTransfer() &&
              this.conferenceService.isConferenceOnHold
          ),

          first(),
          tap(() => {
            this.conferenceService.setConferenceMetadata(
              new TransferMetadata(this.conferenceService.myself, false)
            );
            this.conferenceService.toggleCallHold();
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();

      this.conferenceService.endpointJoined$
        .pipe(
          filter(
            (e) =>
              !this.isMonitoringSupervisor &&
              e.metadata?.roles?.includes(UserRoleEnum.customer) &&
              this.config.publicParam(PublicParam.DISCLAIMER_SNAPSHOT_ENABLED)
          ),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((e) => {
          this.analyticsService.updateInteractionMetrics(this.interaction, {
            [InteractionMetricEnum.termsAcceptedSnapshot]:
              e.metadata?.termsAccepted,
            [InteractionMetricEnum.termsAcceptedAt]:
              e.metadata?.termsAcceptedAt,
          });
        });

      /**
       * Flow that we are covering here:
       * - agent has ended the conversation
       * - the widget got a conversation ended event
       * - customer leaves the conference session with a 'conversationEnded' reason
       * - agent updates the analytics
       *
       * In a Genesys premise setup, if the customer ends the conversation and wraps up the call,
       * the iframe that hosts the app is destroyed, thus leaving no time for the UI to update the analytics
       */
      this.conferenceService.endpointLeft$
        .pipe(
          filter(
            (p) =>
              !this.isMonitoringSupervisor &&
              p?.metadata?.roles?.includes(UserRoleEnum.customer) &&
              p?.metadata?.origin === ConversationOriginEnum.WIDGET &&
              p?.metadata?.reason === TerminateReasonEnum.conversationEnded
          ),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((p) => {
          this.analyticsService.trackCallEnded(this.interaction, true);
        });

      this.snapshot.reviewing$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((e) => {
        this.isReviewingSnapshots = true;
      });

      this.conferenceService.endpointMetadataChanged$
        .pipe(
          filter(
            (e) =>
              !this.isMonitoringSupervisor &&
              !!(e?.newMetadata as IEndpointMetadata)?.customerMetadata
          ),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((e) => {
          const customerMetadata = (e.newMetadata as IEndpointMetadata)
            .customerMetadata;
          this.analyticsService.updateInteractionMetrics(this.interaction, {
            [InteractionMetricEnum.customerMetadata]: customerMetadata,
          });
        });

      /*
      this.subscriptions.add(
        this.conferenceService.getConferenceMetadataSet$
          .pipe(filter((m) => m instanceof CustomerMetricsMetadata))
          .subscribe((meta: CustomerMetricsMetadata) => {
            // if we have metadata and we the customer has marked when he got the ticket,
            // cancel the timer countdown that was set to terminate the call if no customer joined.
            if (!!meta.ticketReceivedAt() && this.waitingForCustomerTimeout) {
              clearTimeout(this.waitingForCustomerTimeout);
              this.waitingForCustomerTimeout = null;
            }
            if (!!this.confirmNotificationCustomerTimeout) {
              this.notification.dismiss(
                this.confirmNotificationCustomerTimeout
              );
              this.confirmNotificationCustomerTimeout = null;
            }
          })
      ); */

      this.recorderOptions = {
        conferenceId: this.interaction.getRoom(),
        conversationId: this.interaction.getId(),
        applicationId: this.application.getId(),
        audio: true,
        video: !this.config.agentParamEnabled(AgentParam.RECORDER_AUDIO_ONLY),
        screen: this.config.agentParamEnabled(
          AgentParam.RECORDER_SCREEN_ENABLED
        ),
      };

      combineLatest([this.store.updated$, this.$recorderStarted])
        .pipe(
          filter(
            () =>
              this.store.streams.length > 0 &&
              this.recorderInfo?.state === RecorderStateEnum.active &&
              !this.recorderTracked
          ),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => {
          this.recorderTracked = true;
          this.analyticsService.trackRecordingStarted(
            this.recorderInfo,
            this.interaction
            );
          });

      this.conferenceService.localEndpointRecovered$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (
            this.recorderTracked &&
            this.recorderInfo?.state === RecorderStateEnum.active
          ) {
            this.analyticsService.recoverRecordingMetrics(
              this.recorderInfo,
              this.interaction
            );
          }
        });

      if (!this.isGuestAgent) {
        this.renewCustomerLink();
      }

      if (
        this.applicationService.getActiveApplication().supportsInteractions()
      ) {
        this.interactionService.eventClient?.ending$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          (conversationId) => {
            debug("conversation ending");
            if (this.interaction?.getId() === conversationId) {
              // mark that the actual interaction has ended but we are still in the room
              // so that we can clear it once we leave
              this.isInteractionEnding = true;
              if (
                !this.isAgentEndingConversation &&
                !this.isReviewingSnapshots &&
                !this.isRating
              ) {
                this.conferenceService.confirmTerminateConferenceRequest();
              }
              if (
                !this.isConversationEndingTracked &&
                !this.isMonitoringSupervisor
              ) {
                this.interaction.setCustomerInvited(false);
                this.analyticsService.trackCallEnding(this.interaction);
                this.isConversationEndingTracked = true;
              }
            }
          }
        );

        this.interactionService.eventClient?.ended$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          (conversationId) => {
            debug("conversation ended from notification");
            if (
              (!this.interaction || // coming from iFrame refresh. not a real world scenario
                this.interaction?.getId() === conversationId) &&
              !this.isReviewingSnapshots &&
              !this.isRating
            ) {
              if (
                !this.isAgentEndingConversation &&
                // support scenarios where we want an active call even if the interaction ends
                // defaults to 'true'
                this.config.agentParamEnabled(
                  AgentParam.AUTO_END_CALL_ON_CONVERSATION_END
                )
              ) {
                this.conferenceService.terminateConferenceRequest(
                  TerminateReasonEnum.conversationEnded
                );
              }
              this.interactionService.clearActiveInteraction();
            }
          }
        );

        this.interactionService.eventClient?.transferring$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
            async (conversationId) => {
              debug("conversation is being transferred");
              if (this.interaction?.getId() === conversationId) {
                this.isInteractionEnding = true;
                this.isInteractionTransferring = true;
                try {
                  await this.conferenceService.setConferenceMetadata(
                    new TransferMetadata(this.conferenceService.myself, true)
                  );
                } catch (ex) {
                  debugError(ex);
                  this.errorHandler.handleError(ex);
                }
                if (!this.isMonitoringSupervisor)
                  this.analyticsService.trackCallTransferred(this.interaction);

                // stop recording, new agent will start a new one, as he must be the new owner and update
                // conference metadata
                if (!!this.recorderInfo) {
                  try {
                    // race condition, sometimes the metadata was not removed because the recorder event did not reach on time
                    this.conferenceService.removeConferenceMetadata(
                      ConferenceMetadataKeyEnum.recorder
                    );
                    await this.recorderService.stop(
                      this.recorderInfo.recorderId,
                      this.recorderInfo.conversationId,
                      this.recorderInfo.instanceId
                    );
                  } catch (ex) {
                    debugError(ex);
                    this.errorHandler.handleError(ex);
                  }
                }

                // if I am pointing, stop
                if (
                  this.pointerService.isStarted &&
                  this.pointerService.isRequester(
                    this.conferenceService.myself.endpoint
                  )
                ) {
                  this.pointerService.end(
                    this.conferenceService.myself.endpoint
                  );
                }

                // if I'm screen sharing, stop
                this.tryStopMyScreenShare();

                // handleCallEnded will terminate an active co-browse session but since the session is already terminated
                // at that point, the conference metadata are not updated
                if (this.cobrowseService.isStarted) {
                  await this.conferenceService.removeConferenceMetadata(
                    ConferenceMetadataKeyEnum.coBrowse
                  );
                }

                // put the call on hold until the new agent joins
                try {
                  if (!this.conferenceService.isConferenceOnHold) {
                    await this.conferenceService.toggleCallHold();
                  }
                } catch (ex) {
                  debugError(ex);
                  this.errorHandler.handleError(ex);
                }

                this.callEnding({
                  terminated: false,
                  terminatedRemotely: false,
                });

                //leave the session
                try {
                  await this.conferenceService.leaveSession(
                    TerminateReasonEnum.transfer
                  );
                } catch (ex) {
                  debugError(ex);
                  this.errorHandler.handleError(ex);
                }
                this.interactionService.clearActiveInteraction();
              }
            }
          );

        this.interactionService.eventClient?.transferred$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          (conversationId) => {
            debug("conversation transferred");
          }
        );
      }

      const authState = AuthState.fromSerializedState(
        this.route.snapshot.queryParamMap.get("state")
      );
      this.shareViaSMSFlow.set(authState.smsFlow);

      this.cdr.detectChanges();
    }

    if (this.isCustomer) {
      this.conferenceService.endpointJoined$
        .pipe(
          filter((e) => e.metadata?.roles?.includes(UserRoleEnum.agent)),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((e) => {
          this.isAgentConnected = true;
          this.cdr.detectChanges();
        });
    }

    if (this.isOriginAppointment) {
      this.appointmentService.appointmentChanged$
        .pipe(
          filter((a) => !!a),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((a) => (this.appointment = a));

      this.tryConnectToAppointmentConversation();
    }

    if (this.isChildWindow) {
      this.windowEventService
        .init(this.interaction.getParentFrameUrl())
        .subscribe((action) => {
          switch (action.type) {
            case windowActionType.THEME_READY:
              this.theme.setCSSVariables(action.payload);
              break;
            case windowActionType.CUSTOMER_METADATA_READY:
              if (this.mediaRules.canSaveCustomerMetadata) {
                this.conferenceService.updateEndpointMetadata({
                  customerMetadata: MediaRulesService.filterCustomerMetadata(
                    this.config.publicParam(
                      PublicParam.CUSTOMER_METADATA_BLACKLIST
                    ),
                    action.payload
                  ),
                });
              }
              break;
            case windowActionType.CALL_QUALITY_OPTIONS:
              this.local.setCallQualityOptions(action.payload);
              break;
          }
        });
      this.windowEventService.sendMessage(new ReadyAction());

      this.conferenceService.conferenceMetadataSet$
        .pipe(
          filter((m) => m instanceof TransferMetadata && m.on),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((m) => {
          this.windowEventService.sendMessage(new TransferAction());
          this.tryStopMyScreenShare();
        });
    }

    if (this.isWidget) {
      this.conferenceService.localStreamPublished$
        .pipe(
          filter(
            (s) =>
              !!s &&
              s.type !== StreamTypes.SCREEN &&
              !this.isFocusedAfterStreamPublished
          )
        )
        .subscribe((s) => {
          this.host.nativeElement.focus();
          if (this.interaction.getOriginMode() === OriginModeEnum.kiosk) {
            trapFocus(this.host.nativeElement);
          }
          this.isFocusedAfterStreamPublished = true;
        });
    }
  }

  ngAfterViewInit(): void {
    // in kiosk mode, the tab navigation also focuses on elements that are behind the video call. We need to trap the focus
    // if (this.interaction.getOriginMode() === OriginModeEnum.kiosk) {
    if (this.isWidget) {
      this.render.setAttribute(this.host.nativeElement, "tabindex", "0");
      this.render.setAttribute(this.host.nativeElement, "role", "group");
      this.host.nativeElement.focus();
    }
  }

  ngOnDestroy() {
    this.handlers = null;
    if (this.isOriginAppointment) {
      this.appointmentService.notifyChange(undefined);
    }
    if (this.waitingForCustomerTimeout) {
      clearTimeout(this.waitingForCustomerTimeout);
      this.waitingForCustomerTimeout = null;
    }
  }

  private observeCallEnd() {
    this.$callEnding
      .pipe(
        combineLatestWith(this.$callEnded),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(async (e) => {
        this.activity.loading(false);

        if (
          this.canEndInteractionOnCallEnd &&
          !this.isInteractionTransferring
        ) {
          this.isAgentEndingConversation = true;

          // covers a scenario where the agent is on the phone
          // and we don't want them to go back to mobile office to end the conversation
          this.interactionService.clearActiveInteraction();
          if (
            this.interaction.getChannel() !== ConversationChannelEnum.unknown
          ) {
            try {
              await this.conversation.leaveConversation(this.interaction);
            } catch (ex) {
              debugError(ex);
              // if it fails, we get stuck on leaving..
            }
          }
        }

        this.goBack();
      });
  }

  private tryStopMyScreenShare() {
    if (
      !!this.store.screenStream &&
      this.store.screenStream.originator.endpoint ===
        this.conferenceService.myself.endpoint
    ) {
      this.local.closeScreenStream();
    }
  }

  private async tryConnectToAppointmentConversation() {
    if (!this.appointment) {
      this.appointment = await this.appointmentService.get(
        this.interaction.getAppointmentId()
      );
    }
    try {
      // update appointment state for both agents and customers
      this.appointmentService.updateState(
        this.interaction.getAppointmentId(),
        "inprogress"
      );

      // only initialize conversation for customers
      if (!this.isCustomer) {
        return;
      }

      const ticket = await this.publicTicketService.getTicket(
        this.route.snapshot.paramMap.get(PARAM_TICKET_ID)
      );

      if (!this.conversation.isConversationInitialised) {
        const config: IConfigOptions =
          this.conversation.prepareConversationConfig(ticket, this.appointment);

        await this.conversation.init(config, ticket.id);
      }
    } catch (ex) {
      // could not load appointment conversation..
    }

    // if conversation was ended by agent, leave the room
    this.conversation.ended$
      // ended$ event comes even if we as a customer end the conversation once we hit the hangup button
      // it also comes if agent ends the conversation
      .pipe(
        filter((_) => !this.isCustomerEndingConversation),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((_) => {
        this.conferenceService.terminateConferenceRequest(
          TerminateReasonEnum.leave
        );
        // we are about to leave as customers, update our state
        this.appointmentService.updateParticipantState(
          this.interaction.getAppointmentId(),
          AppointmentParticipantStateEnum.LEFT
        );
      });
  }

  closeQRPanel() {
    this.QRData = null;
  }

  agentSelected() {
    this.notification.show("now what?");
  }

  callStarted() {
    this.isCallStarted = true;
    // if the rating popup is visible, hide the controls.
    this.ratingService.popupVisibility$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((visible) => {
      this.isRating = visible;
    });

    if (this.isCustomer && this.isWidget) {
      const metrics = new CustomerMetricsMetadata(
        this.conferenceService.myself,
        {
          // ticketReceivedAt: no need to use this. we rely on the getMessages flow of the timeout
          joinedAt: new Date(),
        }
      );
      this.conferenceService.setConferenceMetadata(metrics);
    }
  }

  callPendingRating(isRating: boolean) {
    this.QRData = null;
    if (isRating) {
      this.leaveApppointment(TerminateReasonEnum.leave);
    }
  }

  async callEnded(payload: { reason: TerminateReasonEnum; error?: any }) {
    if (!!payload.error) {
      switch (payload.error.status) {
        case 404:
          this.router.navigate(["/error", ErrorPageCode.ROOM_NOT_FOUND], {
            queryParamsHandling: "preserve",
          });
          break;
        case 403:
          if (payload.error.data.error === "participant limit reached") {
            this.router.navigate(
              ["/error", ErrorPageCode.PARTICIPANT_LIMIT_REACHED],
              { queryParamsHandling: "preserve" }
            );
          } else {
            if (
              window.confirm(
                this.translate.instant("call-ended-error-confirm-reload")
              )
            ) {
              window.location.reload();
              return;
            }
          }
          break;
        default:
          if (
            window.confirm(
              this.translate.instant("call-ended-error-confirm-reload")
            )
          ) {
            window.location.reload();
            return;
          }
          break;
      }
    } else {
      await this.leaveApppointment(payload.reason);

      if (this.isInteractionEnding || this.canEndInteractionOnCallEnd) {
        this.interactionService.clearActiveInteraction();
      }
      this.activity.loading(true);
      this.$callEnded.next(null);
    }
  }

  async callEnding(payload: {
    terminated: boolean;
    terminatedRemotely: boolean;
  }) {
    // stop from firing twice
    if (this.isCallEndingHandled) {
      return;
    }
    this.isCallEndingHandled = true;
    if (
      !this.isMonitoringSupervisor &&
      this.isAgent &&
      !payload.terminatedRemotely
    ) {
      if (payload.terminated) {
        // keep for callback, even though we terminate the call, we must:
        // 1. tell the agent the room is closed
        // 2. in analytics, keep the room id
        if (this.interaction.getType() !== ConversationTypeEnum.callback) {
          this.interaction.setRoom(null);
        }
        if (!this.isInteractionEnding) {
          // if interaction is ending, trackCallEnding will be called at that event
          this.analyticsService.trackCallEnding(this.interaction);
          this.interaction.setCustomerInvited(false);
          this.interactionService.setActiveInteraction(this.interaction);
        }
        // complete appointment
        if (this.isOriginAppointment) {
          this.appointmentService.complete(this.appointment.id);
          this.appointmentService.setAppointmentAsCompletedInInteraction(
            this.interaction.getId()
          );
        }
      }
      // if we are a transferred agent, remove the TAG_INVITED so that we can re-invite
      else if (!this.isMonitoringSupervisor && this.interaction.isTransfer()) {
        this.interaction.setCustomerInvited(false);
        this.analyticsService.trackCallEnding(this.interaction);
      }

      if (
        !(
          this.conferenceService.getConferenceMetadata(
            ConferenceMetadataKeyEnum.transfer
          ) as TransferMetadata
        )?.on
      ) {
        // we need to have enought time to mark the interaction as ended (remove room) if in popup (window will close)
        // otherwise we may end up in a terminated room but not updated interaction with null room
        // this also makes it slow for normal calls so do not wait if not in popup.
        this.device.isPopUp
          ? await this.analyticsService.trackCallEnded(this.interaction)
          : this.analyticsService.trackCallEnded(this.interaction);
      }
    }
    this.$callEnding.next(null);
  }

  goBack() {
    // sometimes we get out of the zone
    this.zone.run(() => {
      if (
        this.isAgent &&
        !!this.config.agentParam(AgentParam.CALL_ENDED_REDIRECT_URL) &&
        !DeviceService.isIFrame && // if we are inside and we redirect, we are stuck
        !this.isMonitoringSupervisor // not monitoring supervisor
      ) {
        return (window.location.href = this.config.agentParam(
          AgentParam.CALL_ENDED_REDIRECT_URL
        ));
      }

      this.activity.loading(false);
      if (this.shouldBeAbleToCreateRoomAgain()) {
        this.router.navigate(["/welcome"], {
          queryParams: { [PARAM_ROOM_ID]: null, [PARAM_CONVERSATION_ID]: null },
          queryParamsHandling: "merge",
        });
      } else {
        this.rtcService
          .unregister()
          .then(() => this.rtcService.logout())
          .catch((ex) => this.errorHandler.handleError(ex));
        this.router.navigate(
          [
            "/thank-you",
            this.isCustomer
              ? UserRoleEnum.customer.toLowerCase()
              : UserRoleEnum.agent.toLowerCase(),
          ],
          { queryParamsHandling: "preserve" }
        );
      }
    });
  }

  private async leaveApppointment(reason: TerminateReasonEnum) {
    if (!this.isOriginAppointment) {
      return;
    }

    if (this.userService.appointmentParticipantId) {
      try {
        // update participant state
        await this.appointmentService.updateParticipantState(
          this.interaction.getAppointmentId(),
          AppointmentParticipantStateEnum.LEFT
        );
      } catch (ex) {
        this.errorHandler.handleError(ex);
      }
    }

    // if (this.conversation.isConversationOriginCustomer(this.appointment.id)) {
    //   this.isCustomerEndingConversation = true;
    //   this.conversation.endConversation();
    //   this.conversation.clearConversationOrigin(this.appointment.id);
    // }
  }

  private shouldBeAbleToCreateRoomAgain(): boolean {
    return this.application.isReady() && this.isAgent && !this.isGuestAgent;
  }

  async exit() {
    if (this.isChildWindow) {
      this.windowEventService.sendMessage(
        new CallEndedAction({ reason: TerminateReasonEnum.leave })
      );
    }
    if (!this.isMonitoringSupervisor && this.isAgent) {
      await this.analyticsService.trackCallEnded(this.interaction);
    }
    this.goBack();
  }

  /**
   * fires once the user has joined the call and is present in the room.
   */
  deviceSetupDoneAndRoomJoined() {
    this.isDeviceSetup = true;
    if (
      this.isAgent &&
      !this.isGuestAgent &&
      !this.isOriginCallback &&
      !this.isOriginAppointment
    ) {
      if (
        (this.isOriginWidget && !this.interaction.isCustomerInvited()) ||
        // if we auto-invite customer, check if we actually have an interaction
        // (not origin=embed and not start calls without interaction)
        (this.config.agentParamEnabled(AgentParam.AUTO_INVITE_CUSTOMER) &&
          !(
            this.isOriginEmbedded &&
            !this.config.agentParamEnabled(
              AgentParam.VIDEO_CALL_REQUIRES_INTERACTION
            )
          ))
      ) {
        // send link to widget as notice
        this.inviteCustomer();
        if (
          this.interaction.getType() !== ConversationTypeEnum.chat &&
          this.interaction.getOrigin() !== ConversationOriginEnum.CHAT
        ) {
          // set a timeout to wait for customer to join, to avoid zombie conversations
          this.waitForCustomerToJoin();
        }
      }
    }

    if (this.isOriginAppointment) {
      if (!!this.userService.appointmentParticipantId) {
        this.appointmentService.updateParticipantState(
          this.interaction.getAppointmentId(),
          AppointmentParticipantStateEnum.JOINED
        );
      }

      if (this.isAgent && !this.isGuestAgent) {
        // we need the room name for the monitor
        this.analyticsService.trackAppointmentStarted(
          this.interaction,
          this.appointment
        );
      }
    }
  }

  private async inviteCustomer() {
    try {
      await this.interactionService.invite(
        this.interaction.getType(),
        this.interaction,
        this.customerTicket
      );
      if (!this.isMonitoringSupervisor) {
        this.interaction.setCustomerInvited(true);
        this.analyticsService.trackInvitationSent(this.interaction);
      }
      this.renewCustomerLink();
    } catch (ex) {
      this.notification.warn("Unable to send invitation to customer.", {
        ttl: -1,
        body: "Please share the room link with the customer by manually copying it and pasting it to the active conversation.",
      });
      this.shareLinkOptionsRef()?.open();
    }
  }

  protected async renewCustomerLink() {
    try {
      const ticketRequest = this.invitationService.prepareTicketRequest(
        this.config.agentParamEnabled(AgentParam.SINGLE_USE_TICKET_ENABLED)
          ? TicketTypeEnum.SingleUseTicket
          : TicketTypeEnum.MultiUseTicket,
        this.config.agentParam(AgentParam.TICKET_LENGTH) ?? 6,
        this.interaction.getRoom(),
        this.interaction.getCustomerId(),
        this.interaction.getId(),
        this.interaction.getCustomerName()
      );
      this.customerTicket = await this.ticketService.createTicket(
        ticketRequest
      );
    } catch (ex) {
      this.errorHandler.handleError(ex);
      this.notification.error("Oups!", {
        body: "Could not create shareable link",
      });
    }
  }

  viewChange(layout: LayoutEnum) {
    // nothing for now
  }

  async saveChatTranscript(): Promise<void> {
    try {
      if (
        !this.recorderInfo ||
        this.recorderInfo.state !== RecorderStateEnum.active
      ) {
        return;
      }

      const transcript = await this.interactionService.getChatTranscript(
        this.interaction.getId(),
        this.interaction.getChannel(),
        false
      );

      if (!transcript || transcript?.chatTranscriptList?.length === 0) {
        return;
      }

      transcript.instanceId = this.recorderInfo.instanceId;
      transcript.recorderId = this.recorderInfo.recorderId;

      await this.recorderService.addTranscript(transcript);
    } catch (ex) {
      debugError(ex);
    }
  }

  remotePopupClosed() {
    // this.shareOpen = true;
    this.notification.show("Auvious", {
      body: this.translate.instant(
        "The customer left the video call. You can send a new invitation."
      ),
    });
    this.isOriginWidgetWithoutVideo = true;
  }

  recorderStarted(recorder: IRecorderInfo) {
    if (!!recorder && !this.isMonitoringSupervisor) {
      this.recorderInfo = recorder;
      this.$recorderStarted.next(recorder);
    }
  }

  async recorderStopped(recorder: IRecorderInfo) {
    try {
      this.recorderInfo = recorder;
      const session = await this.recorderService.getSessionInfo(
        recorder.recorderId,
        recorder.conversationId,
        recorder.instanceId
      );
      if (!!session && !this.isMonitoringSupervisor) {
        this.analyticsService.trackRecordingStopped(session, this.interaction);
      }
    } catch (ex) {
      this.errorHandler.handleError(ex);
    }
  }

  termsAccepted() {
    this.isAccepted = true;
    this.interactionService.acceptTerms();
    this.trySaveAcceptedTerms();
  }

  async termsRejected() {
    if (this.isOriginAppointment) {
      await this.conversation.sendMessage(
        this.translate.instant("Consent not given"),
        "notice"
      );
      this.leaveApppointment(TerminateReasonEnum.termsRejected);
    }
    this.exit();
  }

  trySaveAcceptedTerms() {
    if (this.config.publicParam(PublicParam.DISCLAIMER_SNAPSHOT_ENABLED)) {
      this.interactionService.saveAcceptedTerms(
        this.config.customerParam(CustomerParam.DISCLAIMER_TEXT)
      );
    }
  }

  waitForCustomerToJoin() {
    const timeoutSeconds =
      this.config.agentParam(AgentParam.DISCONNECTED_CUSTOMER_TIMEOUT) * 1000; // it is in seconds
    if (!timeoutSeconds) {
      return;
    }
    this.waitingForCustomerTimeout = setTimeout(async () => {
      // 1. get a history of messages and try to find the ACK message
      try {
        this.waitingForCustomerTimeout = null;
        const messages: IMessage[] = await this.conversation.getMessages(
          this.interaction
        );
        const found = messages
          ?.filter((m) => dayjs(m.timestamp).isSame(new Date(), "day"))
          .find(
            (m) => m.text && m.text.includes(WIDGET_CUSTOMER_CALL_ACCEPTED_ACK)
          );
        if (!!found) {
          return;
        }
        this.confirmNotificationCustomerTimeout = new ConfirmNotification(
          "Customer appears to have left the conversation",
          "Would you like to end the conversation and the call?"
        );
        this.confirmNotificationCustomerTimeout.onConfirmed(async () => {
          try {
            // end conversation
            this.isAgentEndingConversation = true;
            await this.conversation.leaveConversation(this.interaction);
          } catch (ex) {
            // nothing
          }
          //end call
          this.conferenceService.terminateConferenceRequest(
            TerminateReasonEnum.customerDisconnected
          );
        });

        this.notification.notify(this.confirmNotificationCustomerTimeout);
      } catch (ex) {
        this.errorHandler.handleError(ex);
      }
    }, timeoutSeconds);
  }

  /** view getters */

  get canEndInteractionOnCallEnd() {
    return (
      this.isAgent &&
      !this.isMonitoringSupervisor &&
      !this.isGuestAgent &&
      this.config.agentParamEnabled(
        AgentParam.AUTO_END_CONVERSATION_ON_CALL_END
      )
    );
  }

  get isCallbackWaitingVisible() {
    return (
      (this.isOriginCallback || this.isOriginAppointment) &&
      this.isCustomer &&
      !this.isAgentConnected &&
      this.isDeviceSetup &&
      !this.isRating
    );
  }

  get participantId() {
    return this.me.getId();
  }

  get application(): IApplication {
    return this.applicationService.getActiveApplication();
  }

  get isOriginWidget() {
    return this.interaction.getOrigin() === ConversationOriginEnum.WIDGET;
  }

  get isOriginEmbedded() {
    return this.interaction.getOrigin() === ConversationOriginEnum.EMBEDDED;
  }

  get isOriginAppointment() {
    return this.interaction.getOrigin() === ConversationOriginEnum.APPOINTMENT;
  }

  get isOriginPopup() {
    return this.interaction.getOrigin() === ConversationOriginEnum.POPUP;
  }

  get isOriginCallback() {
    return this.interaction.getType() === ConversationTypeEnum.callback;
  }

  get isAIInteraction() {
    return (
      this.interaction.getChannel() === ConversationChannelEnum.aiAssistant
    );
  }

  get isDestinationMobileOffice() {
    return (
      this.interaction.getDestination() ===
      ConversationDestinationEnum.MOBILE_OFFICE
    );
  }

  get isWidget() {
    return DeviceService.isIFrame && !this.isAgent && this.isOriginWidget;
  }

  get isChildWindow() {
    return !this.isAgent && (this.isOriginWidget || this.isOriginPopup);
  }

  get isTrustedConversationDestination() {
    return [ConversationDestinationEnum.MOBILE_OFFICE].includes(
      this.interaction.getDestination()
    );
  }

  get isMessagingAvailable() {
    return (
      this.isMessagingEnabled &&
      this.isCustomer &&
      this.isDeviceSetup &&
      !this.isRating &&
      !this.snapshot.isStarted
    );
  }

  get shouldShowShareOptions() {
    return (
      this.isAgent &&
      !this.isRating &&
      this.isDeviceSetup &&
      !this.isCallEndingHandled &&
      (!this.isGuestAgent || this.isTrustedConversationDestination) &&
      (this.mediaRules.isAgentInvitationAvailable ||
        this.mediaRules.isCustomerInvitationAvailalbe)
    );
  }
}
